import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Node } from 'reactflow';
import { NodeData, Position } from '../../../pages/campaings/flows/types';
import InvalidFeedback from '../../forms/invalid-feedback.component';
import Icon from '../../forms/icon.component';
import Input from '../../forms/input.component';

interface HttpNodeFormProps {
  node: Node<NodeData>;
  onSave: (nodeId: string, data: NodeData, position: Position) => void;
}

const HttpNodeForm = forwardRef<HTMLFormElement, HttpNodeFormProps>(({ node, onSave }, ref) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<NodeData>({
    defaultValues: node.data,
    mode: 'onChange'
  });
  const responses = useWatch({
    control,
    name: 'http.responses',
    defaultValue: node.data.http?.responses || []
  });

  const [tempResponse, setTempResponse] = useState('');

  useEffect(() => {
    reset(node.data);
  }, [node.data, reset]);

  const handleAddResponse = (e: React.KeyboardEvent) => {
    if (['Enter', ','].includes(e.key) && tempResponse.trim()) {
      e.preventDefault();
      const newResponse = tempResponse.trim();
      const currentResponses = responses || [];

      if (!currentResponses.includes(newResponse)) {
        setValue('http.responses', [...currentResponses, newResponse], {
          shouldValidate: true
        });
      }
      setTempResponse('');
    }
  };

  const removeResponse = (index: number) => {
    const newResponses = [...(responses || [])];
    newResponses.splice(index, 1);
    setValue('http.responses', newResponses, {
      shouldValidate: true
    });
  };

  const onSubmit = (data: NodeData) => {
    onSave(node.id, data, node.position);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3">
        <Input
          label="Nombre del nodo"
          placeholder="Escribe el nombre del nodo"
          requiredIndicator="required"
          {...register('title', { required: 'Este campo es requerido' })}
          errors={errors.title?.message}
        />
      </div>

      <div className="mb-3">
        <Input
          label="URL Desarrollo"
          placeholder="Escribe la URL de desarrollo"
          requiredIndicator="required"
          {...register('http.url_dev', { required: 'La URL de desarrollo es requerida' })}
          errors={errors.http?.url_dev?.message}
        />
      </div>

      <div className="mb-3">
        <Input
          label="URL Producción"
          placeholder="Escribe la URL de producción"
          requiredIndicator="required"
          {...register('http.url_prod', { required: 'La URL de producción es requerida' })}
          errors={errors.http?.url_prod?.message}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">
          Respuestas HTTP <span className="required"></span>
        </label>
        <div className="px-2 mb-4 mw-300px">
          <span
            className="badge badge-light-info d-flex align-items-center mb-2 overflow-auto"
            style={{ whiteSpace: 'normal' }}
          >
            <Icon icon="ki-information-3" size="fs-1x ms-2" type="info" />
            <span className="ms-2">Presiona Enter o coma para agregar cada posible respuesta</span>
          </span>
        </div>
        <Input
          type="text"
          value={tempResponse}
          onChange={(e: any) => setTempResponse(e.target.value)}
          onKeyDown={handleAddResponse}
          placeholder="Escribe una respuesta"
          className="form-control form-control-solid mb-3"
        />
        <Controller
          name="http.responses"
          control={control}
          rules={{
            required: 'Debes ingresar al menos una respuesta',
            validate: (value) =>
              (value && value.length > 0) || 'Debes ingresar al menos una respuesta'
          }}
          render={({ fieldState: { error } }) => (
            <>
              <div className={`response-input-container ${error ? 'is-invalid' : ''}`}>
                <div className="d-flex flex-wrap gap-2">
                  {(responses || []).map((res, index) => (
                    <div
                      key={index}
                      className="badge badge-light-primary d-flex align-items-center px-2"
                    >
                      <span>{res}</span>

                      <div onClick={() => removeResponse(index)}>
                        <Icon
                          icon="ki-cross-circle"
                          type="danger"
                          size="fs-1x"
                          className="ms-2 cursor-pointer"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <InvalidFeedback error={error} />
            </>
          )}
        />
      </div>
    </form>
  );
});

export default HttpNodeForm;
